import { formatISO, getYear } from "date-fns";
import {
  _FragmentRefs,
  Environment,
  fetchQuery,
  graphql,
  readInlineData,
} from "relay-runtime";
import { generatePath } from "react-router";
import {
  intersect,
  object,
  pipe,
  string,
  transform,
  tuple,
  union,
} from "valibot";
import { assertNever, invariant } from "./helpers";
import * as BibTeX from "../utils/bibtex";
import {
  datasetVersionCitationFragment$data,
  datasetVersionCitationFragment$key,
} from "./__generated__/datasetVersionCitationFragment.graphql";
import { datasetVersionCitationQuery } from "./__generated__/datasetVersionCitationQuery.graphql";
import {
  datasetVersionAuthorsFragment$data,
  datasetVersionAuthorsFragment$key,
} from "./__generated__/datasetVersionAuthorsFragment.graphql";
import { datasetVersionAuthorsFragmentQuery } from "./__generated__/datasetVersionAuthorsFragmentQuery.graphql";
import { readFragmentData, refetchFragmentData } from "./relay";
import { fetchAllTags } from "./taggable";

const AuthorFragment = graphql`
  fragment datasetVersionAuthorsFragment on DatasetVersion
  @refetchable(queryName: "datasetVersionAuthorsFragmentQuery")
  @argumentDefinitions(
    count: { type: "Int", defaultValue: 10 }
    cursor: { type: "String", defaultValue: null }
  ) {
    authors(first: $count, after: $cursor) {
      totalCount
      edges {
        node {
          __typename
          ... on User {
            id
            displayName
          }
          ... on UnregisteredUser {
            fullName
          }
        }
      }
      pageInfo {
        endCursor
      }
    }
  }
`;

const Fragment = graphql`
  fragment datasetVersionCitationFragment on DatasetVersion @inline {
    version
    publishedAt
    license
    doi {
      full
    }
    dataset {
      name
      shortDescription
      owner {
        username
      }
      localSlug
    }
  }
`;

const Query = graphql`
  query datasetVersionCitationQuery($datasetVersionId: ID!) {
    node(id: $datasetVersionId) {
      ... on DatasetVersion @alias(as: "datasetVersion") {
        ...datasetVersionCitationFragment
        ...datasetVersionAuthorsFragment
        dataset {
          ...taggableFragment
        }
      }
    }
  }
`;

export async function fetchDatasetVersionCitation(
  environment: Environment,
  datasetVersionId: ID,
): Promise<string> {
  const response = await fetchQuery<datasetVersionCitationQuery>(
    environment,
    Query,
    { datasetVersionId },
  ).toPromise();
  const datasetVersion = response?.node?.datasetVersion;
  if (!datasetVersion) {
    throw new Error(`No such dataset version "${datasetVersionId}`);
  }

  const [authors, tags] = await Promise.all([
    fetchAllAuthors(environment, datasetVersion),
    fetchAllTags(environment, datasetVersion.dataset),
  ]);

  return formatDatasetVersionCitation(
    datasetVersion,
    authors.map(getDatasetVersionAuthorDisplayName),
    tags.map(({ node }) => node.name),
  );
}

export async function fetchAllAuthors(
  environment: Environment,
  datasetVersionFragment: datasetVersionAuthorsFragment$key,
) {
  const datasetVersion = await readFragmentData(
    environment,
    AuthorFragment,
    datasetVersionFragment,
  );

  const authors = datasetVersion.authors.edges.slice();

  if (datasetVersion.authors.edges.length < datasetVersion.authors.totalCount) {
    const count =
      datasetVersion.authors.totalCount - datasetVersion.authors.edges.length;
    const cursor = datasetVersion.authors.pageInfo.endCursor;

    const next = await refetchFragmentData<
      datasetVersionAuthorsFragment$key,
      datasetVersionAuthorsFragmentQuery
    >(environment, AuthorFragment, datasetVersionFragment, { count, cursor });
    authors.push(...next.authors.edges);
  }

  return authors;
}

function getDatasetVersionAuthorDisplayName(
  author: datasetVersionAuthorsFragment$data["authors"]["edges"][number],
) {
  invariant(author.node.__typename !== "%other");
  switch (author.node.__typename) {
    case "User":
      return author.node.displayName;
    case "UnregisteredUser":
      return author.node.fullName;
    default:
      assertNever(author.node);
  }
}

/**
 * @param fragmentRef Relay reference to a Dataset version
 * @param overrides Fields which should override data coming from Relay
 * @returns A nicely formatted BibTeX citation
 * @throws {Error} If dataset version has no publish date or DOI.
 */
export function formatDatasetVersionCitation(
  fragmentRef: datasetVersionCitationFragment$key,
  authors: string[],
  tags: string[],
  overrides?: Partial<datasetVersionCitationFragment$data>,
): string {
  const { dataset, doi, license, publishedAt, version } = {
    ...readInlineData(Fragment, fragmentRef),
    ...overrides,
  };

  if (!publishedAt) throw new Error(`Dataset version has no publish date`);
  if (!doi) throw new Error(`Dataset version has no DOI`);

  const url = new URL(
    generatePath("/datasets/:owner/:slug/versions/:version", {
      owner: dataset.owner.username,
      slug: dataset.localSlug,
      version,
    }),
    location.origin,
  );

  return BibTeX.formatEntry(
    "dataset",
    BibTeX.formatCiteKey(
      dataset.owner.username,
      dataset.localSlug,
      String(getYear(publishedAt)),
      version,
    ),
    [
      ["title", dataset.name],
      ["author", authors.join(" and ")],
      ["date", formatISO(publishedAt, { representation: "date" })],
      ["year", String(getYear(publishedAt))],
      ["version", version],
      ["publisher", "Aqora"],
      ["doi", doi.full],
      ["url", url.toString()],
      ["urldate", formatISO(new Date(), { representation: "date" })],
      ["abstract", dataset.shortDescription],
      ["keywords", tags.join(", ")],
      ["license", license],
      ["langid", "english"],
    ],
  );
}

export const DatasetParams = object({
  owner: string(),
  slug: string(),
});

const StrToInt = pipe(
  string(),
  transform((str) => parseInt(str, 10)),
);

const Semver = pipe(
  string(),
  transform((str) => str.split(".", 3)),
  tuple([StrToInt, StrToInt, StrToInt]),
);

export const DatasetVersionParams = intersect([
  DatasetParams,
  object({
    version: Semver,
  }),
]);

export const DatasetContentScaffoldParams = union([
  DatasetVersionParams,
  DatasetParams,
]);
